import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { authAxios, QueryStatus } from "../../../../utils";
import { RootState } from "../../../../redux/store";
import {
  BadgeCreation,
  BadgesList,
  BadgesListApi,
  ReplacementBadgeType,
  UpdateBadgeStatusType,
  UpdateStatusType,
} from "./badgesAPI";
import {
  listAllVehiclesInDbAsync,
  listVehiclesByDepartmentAsync,
} from "../../vehicles/vehiclesSlice";
import { VehiclesListType } from "../../vehicles/vehiclesAPI";

export interface BadgesState {
  badgesListStatus: QueryStatus;
  badgesList: BadgesList[];
  badgesCreationStatus: QueryStatus;
  badgeUpdateStatus: QueryStatus;
  badgeNewStatus: UpdateStatusType | null;
  replacementBadgeStatus: QueryStatus;
  badgesOrderedNumber: number | null;
}

const initialState: BadgesState = {
  badgesListStatus: "idle",
  badgesList: [],
  badgesCreationStatus: "idle",
  badgeUpdateStatus: "idle",
  badgeNewStatus: null,
  replacementBadgeStatus: "idle",
  badgesOrderedNumber: null,
};

export const badgesListAsync = createAsyncThunk(
  "badgesList/call",
  async (companyUuid: string, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    const response = await axios.get<Array<BadgesListApi>>(
      `badges/company/${companyUuid}`,
    );
    const allVehicles: VehiclesListType[] = await thunkAPI
      .dispatch(listAllVehiclesInDbAsync({ companyUuid }))
      .unwrap();

    return response.data.map((b) => {
      const foundVehicle = allVehicles.find((v) => v.uuid === b.vehicleUuid);

      return {
        ...b,
        date: b.createdAt,
        status: b.status === "RETURNED" ? "IN_OPPOSITION" : b.status,
        reference: b.reference?.toLocaleUpperCase(),
        vehicleAnalyticCode: foundVehicle?.analyticCode ?? null,
      };
    });
  },
);

export const badgesListOfADepartmentAsync = createAsyncThunk(
  "badgesListOfADepartment/call",
  async (departmentUuid: string, thunkAPI) => {
    const axios = authAxios({ departmentUuid });
    const response = await axios.get<Array<BadgesListApi>>(
      `departments/badges/department/${departmentUuid}`,
    );
    const allVehicles: VehiclesListType[] = await thunkAPI
      .dispatch(listVehiclesByDepartmentAsync({ departmentUuid }))
      .unwrap();

    return response.data.map((b) => {
      const foundVehicle = allVehicles.find((v) => v.uuid === b.vehicleUuid);

      return {
        ...b,
        date: b.createdAt,
        status: b.status === "RETURNED" ? "IN_OPPOSITION" : b.status,
        reference: b.reference?.toLocaleUpperCase(),
        vehicleAnalyticCode: foundVehicle?.analyticCode ?? null,
      };
    });
  },
);

export const createBadgesAsync = createAsyncThunk(
  "createBadges/call",
  async (
    payload: { companyUuid: string; request: BadgeCreation },
    thunkAPI,
  ) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    await axios.post(
      `badges/bulk_order/company/${payload.companyUuid}`,
      payload.request,
    );
    return payload.request.vehicles.length;
  },
);

export const updateBadgeStatusAsync = createAsyncThunk(
  "updateBadgeStatus/call",
  async (payload: UpdateBadgeStatusType, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    await axios.post(`/badge/${payload.badgeUuid}/update_status`, {
      status: payload.status,
    });
    return payload.status;
  },
);

export const updateBadgeStatusByDepartmentAsync = createAsyncThunk(
  "updateBadgeStatusByDepartment/call",
  async (payload: UpdateBadgeStatusType, thunkAPI) => {
    const departmentUuid = (thunkAPI.getState() as RootState)
      .departmentsForDepartmentUser.currentDepartmentUuid;
    if (!departmentUuid) {
      throw new Error("Department not found");
    }
    const axios = authAxios({ departmentUuid });
    await axios.post(`/departments/badge/${payload.badgeUuid}/update_status`, {
      status: payload.status,
    });
    return payload.status;
  },
);

export const replacementBadgeAsync = createAsyncThunk(
  "replacementBadge/call",
  async (payload: ReplacementBadgeType, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    await axios.post(`/badge/${payload.badgeUuid}/defective_replacement`, {
      companyUuid: payload.companyUuid,
      deliveryAddress: payload.deliveryAddress,
    });
  },
);

export const badgesSlice = createSlice({
  name: "badges",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    resetBadgesCreationStatus(state) {
      state.badgesCreationStatus = "idle";
    },
    resetBadgeUpdateStatus(state) {
      state.badgeUpdateStatus = "idle";
    },
    resetReplacementBadgeStatus(state) {
      state.replacementBadgeStatus = "idle";
    },
    resetBadgesList(state) {
      state.badgesListStatus = "idle";
    },
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder
      .addCase(badgesListAsync.pending, (state) => {
        state.badgesListStatus = "processing";
      })
      .addCase(badgesListAsync.fulfilled, (state, action) => {
        state.badgesList = action.payload;
        state.badgeNewStatus = null;
        state.badgesListStatus = "success";
      })
      .addCase(badgesListAsync.rejected, (state) => {
        state.badgesListStatus = "failed";
      })
      .addCase(badgesListOfADepartmentAsync.pending, (state) => {
        state.badgesListStatus = "processing";
      })
      .addCase(badgesListOfADepartmentAsync.fulfilled, (state, action) => {
        state.badgesList = action.payload;
        state.badgeNewStatus = null;
        state.badgesListStatus = "success";
      })
      .addCase(badgesListOfADepartmentAsync.rejected, (state) => {
        state.badgesListStatus = "failed";
      })
      .addCase(createBadgesAsync.pending, (state) => {
        state.badgesCreationStatus = "processing";
      })
      .addCase(createBadgesAsync.fulfilled, (state, action) => {
        state.badgesCreationStatus = "success";
        state.badgesListStatus = "idle";
        state.badgesOrderedNumber = action.payload;
      })
      .addCase(createBadgesAsync.rejected, (state) => {
        state.badgesCreationStatus = "failed";
      })
      .addCase(updateBadgeStatusAsync.pending, (state) => {
        state.badgeUpdateStatus = "processing";
      })
      .addCase(updateBadgeStatusAsync.fulfilled, (state, action) => {
        state.badgeUpdateStatus = "success";
        state.badgesListStatus = "idle";
        state.badgeNewStatus = action.payload;
      })
      .addCase(updateBadgeStatusAsync.rejected, (state) => {
        state.badgeUpdateStatus = "failed";
      })
      .addCase(updateBadgeStatusByDepartmentAsync.pending, (state) => {
        state.badgeUpdateStatus = "processing";
      })
      .addCase(
        updateBadgeStatusByDepartmentAsync.fulfilled,
        (state, action) => {
          state.badgeUpdateStatus = "success";
          state.badgesListStatus = "idle";
          state.badgeNewStatus = action.payload;
        },
      )
      .addCase(updateBadgeStatusByDepartmentAsync.rejected, (state) => {
        state.badgeUpdateStatus = "failed";
      })
      .addCase(replacementBadgeAsync.pending, (state) => {
        state.replacementBadgeStatus = "processing";
      })
      .addCase(replacementBadgeAsync.fulfilled, (state) => {
        state.replacementBadgeStatus = "success";
        state.badgesListStatus = "idle";
      })
      .addCase(replacementBadgeAsync.rejected, (state) => {
        state.replacementBadgeStatus = "failed";
      });
  },
});

export const selectBadgesList = (state: RootState) => state.badges.badgesList;

export const selectBadgesListStatus = (state: RootState) =>
  state.badges.badgesListStatus;

export const selectBadgesCreationStatus = (state: RootState) =>
  state.badges.badgesCreationStatus;

export const selectBadgeUpdateStatus = (state: RootState) =>
  state.badges.badgeUpdateStatus;

export const selectNewBadgeStatus = (state: RootState) =>
  state.badges.badgeNewStatus;

export const selectReplacementBadgeStatus = (state: RootState) =>
  state.badges.replacementBadgeStatus;

export const selectBadgesOrderedNumber = (state: RootState) =>
  state.badges.badgesOrderedNumber;

export default badgesSlice.reducer;
